<template>
  <div class="tab-content">
    <div class="mb-8">
      <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2 flex items-center">
            <font-awesome-icon icon="calendar-week" class="mr-3 text-blue-600" />
            Time Tables
          </h2>
          <p class="text-gray-500">Manage time tables for each program</p>
        </div>
        <base-button @click="openAddSemesterModal"
          class="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 px-6 py-3 rounded-lg font-semibold shrink-0">
          <font-awesome-icon icon="plus" class="mr-2" />
          Add Time Table
        </base-button>
      </div>

      <div class="mt-6 rounded-2xl border border-gray-200/80 bg-white shadow-sm overflow-hidden">
        <button type="button" @click="toggleSemesterFiltersPanel"
          class="semesters-filter-toggle w-full flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 py-3.5 text-left transition-colors hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          :class="showSemesterFiltersPanel
            ? 'bg-gradient-to-r from-slate-50 to-blue-50/50 border-b border-gray-100 rounded-t-2xl'
            : 'bg-white rounded-2xl'">
          <span class="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700 ring-1 ring-blue-200/60">
              <font-awesome-icon icon="filter" class="text-sm" />
            </span>
            <span class="min-w-0">
              <span class="block text-sm font-semibold text-gray-900">Filter time tables</span>
              <span class="block text-xs text-gray-500 mt-0.5">Optional — narrow the list by institution, campus,
                department, and program</span>
            </span>
            <span v-if="semesterFilterSelectionCount > 0"
              class="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900 ring-1 ring-amber-200/80">
              {{ semesterFilterSelectionCount }} selected
            </span>
          </span>
          <span class="flex shrink-0 items-center gap-2 text-gray-500">
            <span class="text-xs font-medium hidden sm:inline">{{ showSemesterFiltersPanel ? 'Hide' : 'Show' }}</span>
            <font-awesome-icon :icon="showSemesterFiltersPanel ? 'chevron-up' : 'chevron-down'" class="text-lg" />
          </span>
        </button>
        <transition name="semester-filters-panel">
          <div v-show="showSemesterFiltersPanel" class="p-4 sm:p-5 overflow-visible bg-white rounded-b-2xl">
            <div
              class="semesters-tab-filter-grid grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 overflow-visible">
              <div class="semester-filter-cell relative min-w-0">
                <label
                  class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Institution</label>
                <multiselect v-model="filterByEntities" :options="entityFilterOptions" :multiple="true"
                  :close-on-select="false" :searchable="true" label="label" track-by="entity"
                  placeholder="All institutions" select-label="" deselect-label="" selected-label=""
                  class="semesters-filter-multiselect" />
              </div>
              <div class="semester-filter-cell relative min-w-0">
                <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Campus</label>
                <multiselect v-model="filterByCampuses" :options="campusFilterOptions"
                  :disabled="!filterByEntities.length" :multiple="true" :close-on-select="false" :searchable="true"
                  label="label" track-by="id" :placeholder="campusFilterPlaceholder" select-label="" deselect-label=""
                  selected-label="" class="semesters-filter-multiselect" />
              </div>
              <div class="semester-filter-cell relative min-w-0">
                <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Department</label>
                <multiselect v-model="filterByDepartments" :options="departmentFilterOptions"
                  :disabled="!filterByCampuses.length" :multiple="true" :close-on-select="false" :searchable="true"
                  label="label" track-by="id" :placeholder="departmentFilterPlaceholder" select-label=""
                  deselect-label="" selected-label="" class="semesters-filter-multiselect" />
              </div>
              <div class="semester-filter-cell relative min-w-0">
                <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Program</label>
                <multiselect v-model="filterByPrograms" :options="programFilterOptions"
                  :disabled="!filterByDepartments.length" :multiple="true" :close-on-select="false" :searchable="true"
                  label="label" track-by="id" :placeholder="programFilterPlaceholder" select-label="" deselect-label=""
                  selected-label="" class="semesters-filter-multiselect" />
              </div>
            </div>
            <div class="mt-4 flex flex-wrap items-center gap-3">
              <button type="button" @click="applySemesterFilters"
                class="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                <font-awesome-icon icon="search" class="mr-2" />
                Apply filters
              </button>
              <button type="button" @click="clearSemesterFilters" :disabled="!hasActiveSemesterFilters"
                class="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white">
                <font-awesome-icon icon="times" class="mr-2" />
                Clear filters
              </button>
              <p class="text-xs text-gray-500 w-full sm:w-auto sm:flex-1 sm:min-w-[12rem]">Choose institution first,
                then campus, department, and program in order. Click Apply filters to load.</p>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading || loadingData" class="text-center py-12">
      <base-page-spinner type="spin" class-list="h-12"></base-page-spinner>
      <p class="text-gray-500 mt-4">Loading time tables...</p>
    </div>

    <!-- Semesters table -->
    <div v-else>
      <div v-if="semestersTableRows.length > 0"
        class="rounded-2xl border border-gray-200/90 bg-white shadow-sm overflow-hidden">
        <div class="semesters-table-scroll overflow-x-auto">
          <table class="semesters-data-table w-max min-w-full text-left border-collapse text-sm">
            <thead>
              <tr class="bg-gradient-to-r from-slate-50 to-blue-50/50 border-b border-gray-200">
                <th scope="col"
                  class="px-3 py-3.5 text-xs font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap text-center w-12">
                  #</th>
                <th scope="col"
                  class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                  Time Table</th>
                <th scope="col"
                  class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                  Institution</th>
                <th scope="col"
                  class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                  Campus</th>
                <th scope="col"
                  class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                  Department</th>
                <th scope="col"
                  class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                  Program</th>
                <th scope="col"
                  class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-gray-600 whitespace-nowrap">
                  Period</th>
                <th scope="col"
                  class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-gray-600 text-center whitespace-nowrap">
                  Status</th>
                <th scope="col"
                  class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-gray-600 text-right whitespace-nowrap">
                  Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="(row, index) in semestersTableRows" :key="row.semester.id" class="semester-table-row">
                <td class="px-3 py-3 align-middle whitespace-nowrap text-center text-gray-500 tabular-nums w-12">
                  {{ semesterRowNumber(index) }}
                </td>
                <td class="px-4 py-3 align-middle whitespace-nowrap font-semibold">
                  <a v-if="row.semester.token" :href="semesterPageHref(row.semester)" target="_blank"
                    rel="noopener noreferrer"
                    class="text-gray-900 hover:text-blue-700 hover:underline decoration-blue-400 underline-offset-2 cursor-pointer rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                    title="Open time table in new tab">
                    {{ row.meta.semesterName }}
                  </a>
                  <span v-else class="text-gray-900">{{ row.meta.semesterName }}</span>
                </td>
                <td class="px-4 py-3 align-middle whitespace-nowrap text-gray-800">
                  <span
                    class="inline-flex max-w-none items-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-900 ring-1 ring-inset ring-indigo-100 whitespace-nowrap">
                    {{ row.meta.institution }}
                  </span>
                </td>
                <td class="px-4 py-3 align-middle whitespace-nowrap text-gray-700" :title="row.meta.campus">
                  {{ row.meta.campus }}
                </td>
                <td class="px-4 py-3 align-middle whitespace-nowrap text-gray-700" :title="row.meta.department">
                  {{ row.meta.department }}
                </td>
                <td class="px-4 py-3 align-middle whitespace-nowrap font-medium text-gray-900"
                  :title="row.meta.program">
                  {{ row.meta.program }}
                </td>
                <td class="px-4 py-3 align-middle whitespace-nowrap text-xs text-gray-600">
                  <font-awesome-icon icon="calendar-alt" class="mr-1 text-gray-400" />
                  {{ formatDate(row.semester.start_date) }}
                  <span class="text-gray-400 mx-1">→</span>
                  {{ formatDate(row.semester.end_date) }}
                </td>
                <td class="px-4 py-3 align-middle text-center whitespace-nowrap">
                  <span :class="[
                    'inline-flex px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap',
                    row.semester.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                  ]">
                    {{ row.semester.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="px-4 py-3 align-middle text-right whitespace-nowrap">
                  <a v-if="row.semester.token" :href="semesterPageHref(row.semester)" target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center justify-center p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors mr-1"
                    title="Open time table page in new tab">
                    <font-awesome-icon icon="arrow-right" class="text-sm" />
                  </a>
                  <button type="button" @click="editSemester(row.semester)"
                    class="inline-flex items-center justify-center p-2 rounded-lg text-blue-600 hover:bg-blue-100 transition-colors mr-1"
                    title="Edit">
                    <font-awesome-icon icon="edit" class="text-sm" />
                  </button>
                  <button type="button" @click="deleteSemester(row.semester.id)"
                    class="inline-flex items-center justify-center p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                    title="Delete">
                    <font-awesome-icon icon="trash" class="text-sm" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="semestersPaginatorInfo.lastPage > 1" class="px-4 py-3 border-t border-gray-100"
          style="background-color: #F9FAFBCC;">
          <pagination :paginator-info="semestersPaginatorInfo" :set-page="setSemesterListPage" />
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-12 bg-gray-50 rounded-xl">
        <font-awesome-icon icon="calendar-week" class="text-4xl text-gray-400 mb-4" />
        <p class="text-gray-500">No time tables found</p>
        <p class="text-sm text-gray-400 mt-2" v-if="!loading && !loadingData">
          {{ getEmptySemestersMessage() }}
        </p>
      </div>
    </div>

    <!-- Semester Modal -->
    <base-modal v-if="showModal" :title="semesterModalTitle" :width="form.id ? 'min(42rem, calc(100vw - 2rem))' : ''"
      @close="closeModal">
      <div class="p-6 sm:p-8 w-full max-w-3xl mx-auto">
        <!-- Edit flow: context + editable fields only -->
        <template v-if="form.id">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400 mb-4">Location and program</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            <div class="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white p-4 shadow-sm">
              <div class="flex items-start gap-3">
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white">
                  <font-awesome-icon icon="university" />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs font-semibold uppercase tracking-wide text-indigo-600">Institution</p>
                  <p class="mt-1 text-sm font-semibold text-gray-900 leading-snug">{{ editContextDisplay.institution }}
                  </p>
                </div>
              </div>
            </div>
            <div
              class="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50/80 to-white p-4 shadow-sm">
              <div class="flex items-start gap-3">
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white">
                  <font-awesome-icon icon="map-marker-alt" />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs font-semibold uppercase tracking-wide text-emerald-600">Campus</p>
                  <p class="mt-1 text-sm font-semibold text-gray-900 leading-snug">{{ editContextDisplay.campus }}</p>
                </div>
              </div>
            </div>
            <div class="rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50/80 to-white p-4 shadow-sm">
              <div class="flex items-start gap-3">
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-600 text-white">
                  <font-awesome-icon icon="building" />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs font-semibold uppercase tracking-wide text-violet-600">Department</p>
                  <p class="mt-1 text-sm font-semibold text-gray-900 leading-snug">{{ editContextDisplay.department }}
                  </p>
                </div>
              </div>
            </div>
            <div class="rounded-xl border border-amber-100 bg-gradient-to-br from-amber-50/80 to-white p-4 shadow-sm">
              <div class="flex items-start gap-3">
                <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-white">
                  <font-awesome-icon icon="graduation-cap" />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-xs font-semibold uppercase tracking-wide text-amber-700">Program</p>
                  <p class="mt-1 text-sm font-bold text-gray-900 leading-snug">{{ editContextDisplay.program }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-xl border border-gray-200 bg-gray-50/50 p-5 sm:p-6">
            <h3 class="text-sm font-semibold text-gray-900 mb-4 flex items-center">
              <font-awesome-icon icon="edit" class="mr-2 text-blue-600" />
              Edit time table details
            </h3>
            <div class="space-y-5">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <font-awesome-icon icon="calendar-week" class="mr-2 text-blue-600" />
                  Time Table Name
                </label>
                <input v-model="form.name" type="text" :placeholder="semesterNamePlaceholder"
                  class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm" />
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <font-awesome-icon icon="calendar-alt" class="mr-2 text-blue-600" />
                    Start Date
                  </label>
                  <input v-model="form.start_date" type="date"
                    class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm" />
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <font-awesome-icon icon="calendar-check" class="mr-2 text-blue-600" />
                    End Date
                  </label>
                  <input v-model="form.end_date" type="date"
                    class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm" />
                </div>
              </div>
              <div class="flex items-center gap-3 p-4 rounded-xl bg-white border border-gray-200 border-dashed">
                <input id="semester-active-edit" v-model="form.is_active" type="checkbox"
                  class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <label for="semester-active-edit" class="text-sm font-semibold text-gray-700 cursor-pointer">
                  Mark time table as active
                </label>
              </div>
            </div>
          </div>

          <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 mt-2">
            <base-button @click="closeModal"
              class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 w-full sm:w-auto">
              Cancel
            </base-button>
            <base-button @click="updateSemester()"
              class="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl px-6 py-3 rounded-xl font-semibold w-full sm:w-auto">
              Save
            </base-button>
          </div>
        </template>

        <!-- Add flow: wizard -->
        <template v-else>
          <p class="text-sm text-gray-500 mb-6 text-center sm:text-left">
            Step {{ addWizardStep }} of 5 — {{ addWizardStepTitle }}
          </p>

          <!-- Step indicator -->
          <div class="mb-8 overflow-x-auto pb-2 -mx-1">
            <div class="flex items-center justify-between min-w-[280px] sm:min-w-0 gap-1 sm:gap-0">
              <template v-for="stepNum in wizardStepNumbers" :key="'wizard-step-' + stepNum">
                <div class="flex items-center flex-1 min-w-0">
                  <div class="flex flex-col items-center w-full">
                    <div
                      class="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full text-sm font-bold transition-all duration-200 border-2 shrink-0"
                      :class="wizardStepCircleClass(stepNum)">
                      <font-awesome-icon v-if="addWizardStep > stepNum" icon="check" class="text-sm" />
                      <span v-else>{{ stepNum }}</span>
                    </div>
                    <span
                      class="text-[10px] sm:text-xs font-medium mt-2 text-center leading-tight px-0.5 line-clamp-2 w-full"
                      :class="addWizardStep === stepNum ? 'text-blue-700' : (addWizardStep > stepNum ? 'text-green-700' : 'text-gray-400')">
                      {{ wizardStepLabels[stepNum - 1] }}
                    </span>
                  </div>
                  <div v-if="stepNum < 5"
                    class="h-0.5 flex-1 mx-1 sm:mx-2 mb-6 sm:mb-7 rounded transition-colors duration-200 min-w-[8px]"
                    :class="addWizardStep > stepNum ? 'bg-green-400' : 'bg-gray-200'" />
                </div>
              </template>
            </div>
          </div>

          <!-- Step panels -->
          <div class="min-h-[220px] transition-all duration-200">
            <!-- Step 1: Institution -->
            <div v-show="addWizardStep === 1"
              class="rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-blue-50/40 p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                <font-awesome-icon icon="university" class="mr-2 text-blue-600" />
                Choose institution
              </h3>
              <p class="text-sm text-gray-600 mb-5">Select the institution this time table belongs to.</p>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Institution <span class="text-red-500">*</span>
              </label>
              <select v-model="formInstitutionId" @change="onFormInstitutionChange"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm">
                <option value="">Select Institution</option>
                <option v-for="institution in filteredInstitutionsForForm" :key="institution.id"
                  :value="institution.id">
                  {{ getInstitutionDisplayName(institution) }}
                </option>
              </select>
            </div>

            <!-- Step 2: Campus -->
            <div v-show="addWizardStep === 2"
              class="rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-emerald-50/30 p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                <font-awesome-icon icon="map-marker-alt" class="mr-2 text-emerald-600" />
                Choose campus
              </h3>
              <p class="text-sm text-gray-600 mb-5">Pick the campus for this program department.</p>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Campus <span class="text-red-500">*</span>
              </label>
              <select v-model="formCampus" @change="onFormCampusChange"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm"
                :disabled="!formInstitutionId">
                <option value="">Select Campus</option>
                <option v-for="campus in formFilteredCampuses" :key="'c-' + campus.id" :value="campus.name">
                  {{ campus.name }}
                </option>
              </select>
            </div>

            <!-- Step 3: Department -->
            <div v-show="addWizardStep === 3"
              class="rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-violet-50/30 p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                <font-awesome-icon icon="building" class="mr-2 text-violet-600" />
                Choose department
              </h3>
              <p class="text-sm text-gray-600 mb-5">Select the academic department.</p>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Department <span class="text-red-500">*</span>
              </label>
              <select v-model="formProgramDepartmentId" @change="onFormDepartmentChange"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm"
                :disabled="!formCampus">
                <option value="">Select Department</option>
                <option v-for="dept in formFilteredDepartments" :key="dept.id" :value="dept.id">
                  {{ dept.name }}
                </option>
              </select>
            </div>

            <!-- Step 4: Program -->
            <div v-show="addWizardStep === 4"
              class="rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-amber-50/30 p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                <font-awesome-icon icon="graduation-cap" class="mr-2 text-amber-600" />
                Choose program
              </h3>
              <p class="text-sm text-gray-600 mb-5">Select the program this time table is for.</p>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Program <span class="text-red-500">*</span>
              </label>
              <select v-model="form.program_id"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm"
                :disabled="!formProgramDepartmentId">
                <option value="">Select Program</option>
                <option v-for="program in formFilteredPrograms" :key="program.id" :value="program.id">
                  {{ program.name }}
                </option>
              </select>
            </div>

            <!-- Step 5: Time table details -->
            <div v-show="addWizardStep === 5"
              class="rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 to-blue-50/50 p-6 shadow-sm space-y-5">
              <div>
                <h3 class="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                  <font-awesome-icon icon="calendar-week" class="mr-2 text-blue-600" />
                  Time Table details
                </h3>
                <p class="text-sm text-gray-600">Enter the time table name, schedule, and status.</p>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <font-awesome-icon icon="calendar-week" class="mr-2 text-blue-600" />
                  Time Table Name
                </label>
                <input v-model="form.name" type="text" :placeholder="semesterNamePlaceholder"
                  class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm" />
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <font-awesome-icon icon="calendar-alt" class="mr-2 text-blue-600" />
                    Start Date
                  </label>
                  <input v-model="form.start_date" type="date"
                    class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm" />
                </div>
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <font-awesome-icon icon="calendar-check" class="mr-2 text-blue-600" />
                    End Date
                  </label>
                  <input v-model="form.end_date" type="date"
                    class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm" />
                </div>
              </div>
              <div class="flex items-center gap-3 p-4 rounded-xl bg-white border border-gray-200 border-dashed">
                <input id="semester-active-wizard" v-model="form.is_active" type="checkbox"
                  class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <label for="semester-active-wizard" class="text-sm font-semibold text-gray-700 cursor-pointer">
                  Mark time table as active
                </label>
              </div>
            </div>
          </div>

          <!-- Wizard footer -->
          <div
            class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 mt-2 border-t border-gray-200">
            <base-button @click="closeModal"
              class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 w-full sm:w-auto">
              Cancel
            </base-button>
            <div class="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto sm:justify-end">
              <base-button v-if="addWizardStep > 1" @click="wizardBack"
                class="bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-800 px-6 py-3 rounded-xl font-semibold transition-all duration-200 w-full sm:w-auto">
                <font-awesome-icon icon="arrow-left" class="mr-2" />
                Back
              </base-button>
              <base-button v-if="addWizardStep < 5" @click="wizardNext" :disabled="!addWizardCanProceed"
                class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed">
                Next
                <font-awesome-icon icon="arrow-right" class="ml-2" />
              </base-button>
              <base-button v-if="addWizardStep === 5" @click="saveSemester()"
                class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md w-full sm:w-auto">
                <font-awesome-icon icon="check" class="mr-2" />
                Save
              </base-button>
            </div>
          </div>
        </template>
      </div>
    </base-modal>
  </div>
</template>

<script>
import { gql } from 'graphql-tag';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import { formatDate, getDisplayEntityCode } from '../utils';
import Pagination from '../../../components/shared/pagination.vue';

export default {
  name: 'SemestersTab',
  components: {
    Multiselect,
    Pagination
  },
  props: {
    institutions: {
      type: Array,
      default: () => []
    },
    allCampuses: {
      type: Array,
      default: () => []
    },
    allDepartments: {
      type: Array,
      default: () => []
    },
    allPrograms: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      allSemesters: [],
      semesterListPaginatorInfo: null,
      filterByEntities: [],
      filterByCampuses: [],
      filterByDepartments: [],
      filterByPrograms: [],
      showModal: false,
      formInstitutionId: '',
      formCampus: '',
      formProgramDepartmentId: '',
      form: {
        id: null,
        program_id: '',
        name: '',
        start_date: '',
        end_date: '',
        is_active: true
      },
      loadingData: false,
      addWizardStep: 1,
      wizardStepLabels: ['Institution', 'Campus', 'Department', 'Program', 'Time Table details'],
      wizardStepNumbers: [1, 2, 3, 4, 5],
      editingSemesterContext: null,
      semesterListPage: 1,
      semesterListPerPage: 10,
      showSemesterFiltersPanel: false
    };
  },
  computed: {
    semesterModalTitle() {
      if (!this.form.id) {
        return 'Add Time Table';
      }
      const n = this.form.name && String(this.form.name).trim();
      return n ? n : 'Time Table';
    },
    editContextDisplay() {
      const dash = '—';
      const ctx = this.editingSemesterContext;
      if (ctx && ctx.program && ctx.program.program_department) {
        const pd = ctx.program.program_department;
        const prog = ctx.program;
        return {
          institution: pd.entity ? getDisplayEntityCode(pd.entity) : dash,
          campus: pd.campus ? String(pd.campus).trim() : dash,
          department: pd.name ? String(pd.name).trim() : dash,
          program: prog.name ? String(prog.name).trim() : dash
        };
      }
      const programId = this.form.program_id;
      const p = this.allPrograms && this.allPrograms.length
        ? this.allPrograms.find(x => String(x.id) === String(programId))
        : null;
      if (p && p.program_department) {
        const pd = p.program_department;
        return {
          institution: pd.entity ? getDisplayEntityCode(pd.entity) : dash,
          campus: pd.campus ? String(pd.campus).trim() : dash,
          department: pd.name ? String(pd.name).trim() : dash,
          program: p.name ? String(p.name).trim() : dash
        };
      }
      return {
        institution: dash,
        campus: dash,
        department: dash,
        program: dash
      };
    },
    hasActiveSemesterFilters() {
      return this.filterByEntities.length > 0 ||
        this.filterByCampuses.length > 0 ||
        this.filterByDepartments.length > 0 ||
        this.filterByPrograms.length > 0;
    },
    semesterFilterSelectionCount() {
      return this.filterByEntities.length +
        this.filterByCampuses.length +
        this.filterByDepartments.length +
        this.filterByPrograms.length;
    },
    campusFilterPlaceholder() {
      return this.filterByEntities.length ? 'All campuses' : 'Select institution first';
    },
    departmentFilterPlaceholder() {
      return this.filterByCampuses.length ? 'All departments' : 'Select campus first';
    },
    programFilterPlaceholder() {
      return this.filterByDepartments.length ? 'All programs' : 'Select department first';
    },
    entityFilterOptions() {
      if (!this.filteredInstitutionsForForm || !this.filteredInstitutionsForForm.length) {
        return [];
      }
      return this.filteredInstitutionsForForm.map(inst => ({
        entity: inst.entity,
        label: this.getInstitutionDisplayName(inst)
      }));
    },
    campusFilterOptions() {
      if (!this.filterByEntities.length) {
        return [];
      }
      if (!this.allCampuses || !Array.isArray(this.allCampuses)) {
        return [];
      }
      const allowed = {};
      this.filteredInstitutionsForForm.forEach(inst => {
        if (inst.entity) {
          allowed[inst.entity] = true;
        }
      });
      const es = {};
      this.filterByEntities.forEach(e => {
        es[e.entity] = true;
      });
      let list = this.allCampuses.filter(c => {
        return c.entity && allowed[c.entity] && es[c.entity];
      });
      const seen = {};
      list = list.filter(function (c) {
        const key = String(c.id);
        if (seen[key]) {
          return false;
        }
        seen[key] = true;
        return true;
      });
      return list.map(c => ({
        id: c.id,
        name: c.name,
        entity: c.entity,
        label: c.name || ''
      })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    departmentFilterOptions() {
      if (!this.filterByCampuses.length) {
        return [];
      }
      if (!this.allDepartments || !Array.isArray(this.allDepartments)) {
        return [];
      }
      const allowed = {};
      this.filteredInstitutionsForForm.forEach(inst => {
        if (inst.entity) {
          allowed[inst.entity] = true;
        }
      });
      const es = {};
      this.filterByEntities.forEach(e => {
        es[e.entity] = true;
      });
      const campusNames = this.filterByCampuses.map(c => String(c.name).trim());
      let list = this.allDepartments.filter(d => {
        if (!d || !allowed[d.entity]) {
          return false;
        }
        if (this.filterByEntities.length && es[d.entity] === undefined) {
          return false;
        }
        const dc = d.campus ? String(d.campus).trim() : '';
        return campusNames.indexOf(dc) !== -1;
      });
      const deptById = {};
      list.forEach(function (d) {
        if (!d || d.id === undefined || d.id === null) {
          return;
        }
        const key = String(d.id);
        if (!deptById[key]) {
          deptById[key] = d;
        }
      });
      list = Object.keys(deptById).map(function (k) {
        return deptById[k];
      });
      return list.map(d => ({
        id: d.id,
        name: d.name,
        entity: d.entity,
        campus: d.campus,
        label: d.name || ''
      })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    programFilterOptions() {
      if (!this.filterByDepartments.length) {
        return [];
      }
      if (!this.allPrograms || !Array.isArray(this.allPrograms)) {
        return [];
      }
      const ids = this.filterByDepartments.map(d => String(d.id));
      let list = this.allPrograms.filter(p => {
        return p.program_department_id && ids.indexOf(String(p.program_department_id)) !== -1;
      });
      const progById = {};
      list.forEach(function (p) {
        if (!p || p.id === undefined || p.id === null) {
          return;
        }
        const key = String(p.id);
        if (!progById[key]) {
          progById[key] = p;
        }
      });
      list = Object.keys(progById).map(function (k) {
        return progById[k];
      });
      return list.map(p => ({
        id: p.id,
        name: p.name,
        program_department_id: p.program_department_id,
        label: p.name || ''
      })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    addWizardStepTitle() {
      return this.wizardStepLabels[this.addWizardStep - 1] || '';
    },
    addWizardCanProceed() {
      switch (this.addWizardStep) {
        case 1:
          return !!this.formInstitutionId;
        case 2:
          return !!this.formCampus;
        case 3:
          return !!this.formProgramDepartmentId;
        case 4:
          return !!this.form.program_id;
        default:
          return true;
      }
    },
    filteredInstitutionsForForm() {
      const allowedEntities = ['FCHS', 'ADPOLY', 'IAT', 'ATHS', 'ADVETI'];
      if (!this.institutions || !Array.isArray(this.institutions)) {
        return [];
      }
      return this.institutions.filter(institution => {
        const entityCode = institution.entity ? institution.entity.trim().toUpperCase() : '';
        return allowedEntities.indexOf(entityCode) !== -1;
      });
    },
    formFilteredCampuses() {
      if (!this.formInstitutionId) {
        return [];
      }
      if (!this.allCampuses || !Array.isArray(this.allCampuses)) {
        return [];
      }
      const institution = this.institutions.find(i => String(i.id) === String(this.formInstitutionId));
      if (!institution) {
        return [];
      }
      return this.allCampuses.filter(campus => {
        return campus.entity === institution.entity || campus.entity === institution.name;
      });
    },
    formFilteredDepartments() {
      if (!this.formCampus) {
        return [];
      }
      if (!this.allDepartments || !Array.isArray(this.allDepartments)) {
        return [];
      }
      const institution = this.institutions.find(i => String(i.id) === String(this.formInstitutionId));
      if (!institution) {
        return [];
      }
      const campusTrim = String(this.formCampus).trim();
      const list = this.allDepartments.filter(dept => {
        if (!dept || dept.entity !== institution.entity) {
          return false;
        }
        const deptCampus = dept.campus ? String(dept.campus).trim() : '';
        return deptCampus === campusTrim;
      });
      return list.slice().sort((a, b) => {
        const na = a.name || '';
        const nb = b.name || '';
        return na.localeCompare(nb);
      });
    },
    formFilteredPrograms() {
      if (!this.formProgramDepartmentId) {
        return [];
      }
      if (!this.allPrograms || !Array.isArray(this.allPrograms)) {
        return [];
      }
      const deptId = String(this.formProgramDepartmentId);
      const list = this.allPrograms.filter(p => {
        return p.program_department_id && String(p.program_department_id) === deptId;
      });
      return list.slice().sort((a, b) => {
        const na = a.name || '';
        const nb = b.name || '';
        return na.localeCompare(nb);
      });
    },
    semesterNamePlaceholder() {
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;
      const academicYear = `${currentYear}-${nextYear}`;
      const seasonExample = `Fall Semester ${academicYear}`;
      if (this.form.program_id) {
        const program = this.allPrograms.find(p => String(p.id) === String(this.form.program_id));
        if (program && program.name) {
          return `${program.name} - ${seasonExample}`;
        }
      }
      return `e.g., ${seasonExample} or Spring Semester ${academicYear}`;
    },
    semestersTableRows() {
      const self = this;
      const list = this.allSemesters && Array.isArray(this.allSemesters) ? this.allSemesters : [];
      return list.map(function (semester) {
        return {
          semester: semester,
          meta: self.buildSemesterRowMeta(semester)
        };
      });
    },
    semestersPaginatorInfo() {
      const pi = this.semesterListPaginatorInfo;
      if (pi) {
        return pi;
      }
      return {
        firstItem: 0,
        lastItem: 0,
        total: 0,
        currentPage: 1,
        lastPage: 1,
        hasMorePages: false,
        perPage: this.semesterListPerPage
      };
    }
  },
  watch: {
    filterByEntities() {
      this.filterByCampuses = [];
      this.filterByDepartments = [];
      this.filterByPrograms = [];
      this.semesterListPage = 1;
    },
    filterByCampuses() {
      this.filterByDepartments = [];
      this.filterByPrograms = [];
      this.semesterListPage = 1;
    },
    filterByDepartments() {
      this.filterByPrograms = [];
      this.semesterListPage = 1;
    },
    filterByPrograms() {
      this.semesterListPage = 1;
    }
  },
  methods: {
    toggleSemesterFiltersPanel() {
      this.showSemesterFiltersPanel = !this.showSemesterFiltersPanel;
    },
    applySemesterFilters() {
      this.semesterListPage = 1;
      this.loadSemesters();
    },
    semesterPageHref(semester) {
      if (!semester || !semester.token) {
        return '#';
      }
      return this.$router.resolve({
        name: 'Timetable Semester',
        params: { token: String(semester.token) }
      }).href;
    },
    formatDate,
    getInstitutionDisplayName(institution) {
      const entityCode = institution.entity ? institution.entity.trim().toUpperCase() : '';
      const entityMap = {
        FCHS: 'FCHS',
        ADPOLY: 'ADPOLY',
        IAT: 'IAT',
        ATHS: 'ATS',
        ATS: 'ATS',
        ADVETI: 'ADVETI'
      };
      if (entityMap[entityCode]) {
        return entityMap[entityCode];
      }
      const requiredInstitutions = ['FCHS', 'ADPOLY', 'IAT', 'ADVETI'];
      if (requiredInstitutions.indexOf(entityCode) !== -1) {
        return entityCode;
      }
      return institution.name || entityCode || 'Unknown';
    },
    onFormInstitutionChange() {
      this.formCampus = '';
      this.formProgramDepartmentId = '';
      this.form.program_id = '';
    },
    onFormCampusChange() {
      this.formProgramDepartmentId = '';
      this.form.program_id = '';
    },
    onFormDepartmentChange() {
      this.form.program_id = '';
    },
    wizardStepCircleClass(stepNum) {
      if (this.addWizardStep > stepNum) {
        return 'border-green-500 bg-green-500 text-white';
      }
      if (this.addWizardStep === stepNum) {
        return 'border-blue-600 bg-blue-600 text-white ring-4 ring-blue-100';
      }
      return 'border-gray-200 bg-white text-gray-400';
    },
    wizardNext() {
      if (!this.addWizardCanProceed) {
        return;
      }
      if (this.addWizardStep < 5) {
        this.addWizardStep += 1;
      }
    },
    wizardBack() {
      if (this.addWizardStep > 1) {
        this.addWizardStep -= 1;
      }
    },
    openAddSemesterModal() {
      this.editingSemesterContext = null;
      this.addWizardStep = 1;
      this.formInstitutionId = '';
      this.formCampus = '';
      this.formProgramDepartmentId = '';
      this.form = {
        id: null,
        program_id: '',
        name: '',
        start_date: '',
        end_date: '',
        is_active: true
      };
      this.showModal = true;
    },
    getEmptySemestersMessage() {
      if (this.hasActiveSemesterFilters) {
        return 'No time tables match the current filters. Adjust selections and click Apply filters, or clear filters.';
      }
      return 'No time tables have been added yet.';
    },
    clearSemesterFilters() {
      this.filterByEntities = [];
      this.filterByCampuses = [];
      this.filterByDepartments = [];
      this.filterByPrograms = [];
      this.semesterListPage = 1;
      this.loadSemesters();
    },
    setSemesterListPage(page) {
      const info = this.semesterListPaginatorInfo;
      const lastPage = info && info.lastPage ? info.lastPage : 1;
      let p = parseInt(page, 10);
      if (isNaN(p) || p < 1) {
        p = 1;
      }
      if (p > lastPage) {
        p = lastPage;
      }
      this.semesterListPage = p;
      this.loadSemesters();
    },
    semesterRowNumber(index) {
      const info = this.semestersPaginatorInfo;
      return (info.currentPage - 1) * info.perPage + index + 1;
    },
    buildSemesterRowMeta(semester) {
      const dash = '—';
      const pd = semester.program && semester.program.program_department;
      const prog = semester.program;
      return {
        semesterName: semester.name && String(semester.name).trim() ? semester.name : dash,
        institution: pd && pd.entity ? getDisplayEntityCode(pd.entity) : dash,
        campus: pd && pd.campus ? String(pd.campus).trim() : dash,
        department: pd && pd.name ? String(pd.name).trim() : dash,
        program: prog && prog.name ? String(prog.name).trim() : dash
      };
    },
    async loadSemesters() {
      this.loadingData = true;
      try {
        const filterEntities = this.filterByEntities.map(function (e) {
          return e.entity;
        });
        const filterCampuses = this.filterByCampuses.map(function (c) {
          return String(c.name).trim();
        });
        const filterProgramDepartmentIds = this.filterByDepartments.map(function (d) {
          return d.id;
        });
        const filterProgramIds = this.filterByPrograms.map(function (p) {
          return p.id;
        });
        const response = await this.$apollo.query({
          query: gql`
            query ProgramSemestersPaged(
              $page: Int
              $per_page: Int
              $filter_entities: [String!]
              $filter_campuses: [String!]
              $filter_program_department_ids: [ID!]
              $filter_program_ids: [ID!]
            ) {
              programSemesters(
                page: $page
                per_page: $per_page
                filter_entities: $filter_entities
                filter_campuses: $filter_campuses
                filter_program_department_ids: $filter_program_department_ids
                filter_program_ids: $filter_program_ids
              ) {
                error
                error_code
                message
                data {
                  id
                  token
                  program_id
                  name
                  start_date
                  end_date
                  is_active
                  program {
                    id
                    name
                    program_department {
                      id
                      entity
                      campus
                      name
                    }
                  }
                }
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
              }
            }
          `,
          variables: {
            page: this.semesterListPage,
            per_page: this.semesterListPerPage,
            filter_entities: filterEntities,
            filter_campuses: filterCampuses,
            filter_program_department_ids: filterProgramDepartmentIds,
            filter_program_ids: filterProgramIds
          },
          fetchPolicy: 'network-only'
        });

        if (response.data.programSemesters.error) {
          console.error('Failed to load semesters:', response.data.programSemesters.message);
          this.allSemesters = [];
          this.semesterListPaginatorInfo = null;
          return;
        }

        this.allSemesters = response.data.programSemesters.data || [];
        this.semesterListPaginatorInfo = response.data.programSemesters.paginatorInfo || null;
      } catch (error) {
        console.error('Failed to load semesters:', error);
        this.allSemesters = [];
        this.semesterListPaginatorInfo = null;
      } finally {
        this.loadingData = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.editingSemesterContext = null;
      this.addWizardStep = 1;
      this.formInstitutionId = '';
      this.formCampus = '';
      this.formProgramDepartmentId = '';
      this.form = {
        id: null,
        program_id: '',
        name: '',
        start_date: '',
        end_date: '',
        is_active: true
      };
    },
    async saveSemester() {
      if (!this.form.program_id) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Required',
          text: 'Please select institution, campus, department, and program.'
        });
        return;
      }
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateProgramSemester($input: CreateProgramSemesterInput!) {
              createProgramSemester(input: $input) {
                error
                error_code
                message
                data {
                  id
                  token
                  program_id
                  name
                  start_date
                  end_date
                  is_active
                  program {
                    id
                    name
                    program_department {
                      id
                      entity
                      campus
                      name
                    }
                  }
                }
              }
            }
          `,
          variables: {
            input: {
              program_id: this.form.program_id,
              name: this.form.name,
              start_date: this.form.start_date,
              end_date: this.form.end_date,
              is_active: this.form.is_active !== undefined ? this.form.is_active : true
            }
          }
        });

        if (response.data.createProgramSemester.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.createProgramSemester.message
          });
          return;
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer);
            toast.addEventListener('mouseleave', this.$swal.resumeTimer);
          }
        });
        Toast.fire({
          icon: 'success',
          title: 'Time table created successfully.'
        });

        this.closeModal();
        await this.$nextTick();
        this.loadSemesters();
        this.$emit('refresh', 'semesters');
      } catch (error) {
        console.error('Failed to save semester:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save time table. Please try again.'
        });
      }
    },
    async updateSemester() {
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateProgramSemester($input: UpdateProgramSemesterInput!) {
              updateProgramSemester(input: $input) {
                error
                error_code
                message
                data {
                  id
                  token
                  program_id
                  name
                  start_date
                  end_date
                  is_active
                  program {
                    id
                    name
                    program_department {
                      id
                      entity
                      campus
                      name
                    }
                  }
                }
              }
            }
          `,
          variables: {
            input: {
              id: this.form.id,
              name: this.form.name,
              start_date: this.form.start_date,
              end_date: this.form.end_date,
              is_active: this.form.is_active
            }
          }
        });

        if (response.data.updateProgramSemester.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.updateProgramSemester.message
          });
          return;
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer);
            toast.addEventListener('mouseleave', this.$swal.resumeTimer);
          }
        });
        Toast.fire({
          icon: 'success',
          title: 'Time table updated successfully.'
        });

        this.closeModal();
        await this.$nextTick();
        this.loadSemesters();
        this.$emit('refresh', 'semesters');
      } catch (error) {
        console.error('Failed to update semester:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update time table. Please try again.'
        });
      }
    },
    editSemester(semester) {
      this.editingSemesterContext = semester;
      this.formInstitutionId = '';
      this.formCampus = '';
      this.formProgramDepartmentId = '';
      this.form = {
        id: semester.id,
        program_id: semester.program_id,
        name: semester.name,
        start_date: semester.start_date,
        end_date: semester.end_date,
        is_active: semester.is_active
      };
      this.showModal = true;
    },
    async deleteSemester(id) {
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: 'This will permanently delete the time table.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteProgramSemester($id: ID!) {
              deleteProgramSemester(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: {
            id: id
          }
        });

        if (response.data.deleteProgramSemester.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.deleteProgramSemester.message
          });
          return;
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer);
            toast.addEventListener('mouseleave', this.$swal.resumeTimer);
          }
        });
        Toast.fire({
          icon: 'success',
          title: 'Time table has been deleted.'
        });

        await this.$nextTick();
        this.loadSemesters();
        this.$emit('refresh', 'semesters');
      } catch (error) {
        console.error('Failed to delete semester:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete time table. Please try again.'
        });
      }
    }
  },
  mounted() {
    this.loadSemesters();
  }
};
</script>

<style scoped>
.semesters-filter-multiselect :deep(.multiselect__tags) {
  min-height: 2.75rem;
  border-radius: 0.75rem;
  border-color: #e5e7eb;
  padding-top: 0.5rem;
}

.semesters-filter-multiselect :deep(.multiselect__input),
.semesters-filter-multiselect :deep(.multiselect__single) {
  font-size: 0.875rem;
}

.semesters-filter-multiselect :deep(.multiselect__tag) {
  border-radius: 0.5rem;
  background: #eff6ff;
  color: #1d4ed8;
}

.semesters-filter-multiselect :deep(.multiselect__tag-icon::after) {
  color: #1d4ed8;
}

.semesters-filter-multiselect :deep(.multiselect--active .multiselect__tags) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

/* Dropdown panel: stack above siblings, opaque background, shadow */
.semesters-filter-multiselect :deep(.multiselect__content-wrapper) {
  z-index: 300 !important;
  background: #ffffff;
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
}

:deep(.semesters-filter-multiselect.multiselect--active) {
  position: relative;
  z-index: 1;
}

.semesters-filter-multiselect :deep(.multiselect__content) {
  background: #ffffff;
}

/* Horizontal scroll: smooth on touch devices */
.semesters-table-scroll {
  -webkit-overflow-scrolling: touch;
}

/* Row hover: solid blue-100 (#dbeafe) — avoids bg-* / opacity utilities not in build */
.semester-table-row {
  transition: background-color 0.15s ease;
}

.semester-table-row:hover {
  background-color: #dbeafe;
}

/* Collapsible filter panel */
.semester-filters-panel-enter-active,
.semester-filters-panel-leave-active {
  transition: opacity 0.2s ease;
}

.semester-filters-panel-enter,
.semester-filters-panel-leave-to {
  opacity: 0;
}
</style>

<style>
/* Unscoped: whole cell must stack above following grid rows when dropdown is open (:has). */
.semesters-tab-filter-grid>.semester-filter-cell {
  position: relative;
  z-index: 1;
}

.semesters-tab-filter-grid>.semester-filter-cell:has(.multiselect--active) {
  z-index: 400;
}
</style>
